import type { KeyboardEvent } from 'react';

import { CheckIcon } from '../../icons/CheckIcon';
import { CloseIcon } from '../../icons/CloseIcon';
import {
  AppliedAmount,
  AppliedBody,
  AppliedCard,
  AppliedCode,
  AppliedDescription,
  AppliedList,
  ApplyButton,
  ErrorRow,
  Field,
  FieldStack,
  InlineLabel,
  Input,
  RemoveIconButton,
  SuccessCheck,
  SuccessRow,
  Wrapper,
} from './DiscountCode.styles';
import type { DiscountCodeProps } from './DiscountCode.types';

export function DiscountCode({
  kind = 'promo',
  applied,
  successMessage,
  value,
  onChange,
  onApply,
  onRemove,
  placeholder,
  applyLabel,
  removeLabel,
  error,
  disabled,
  className,
}: DiscountCodeProps) {
  const appliedList = applied ?? [];
  const canApply = !disabled && value.trim().length > 0;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && canApply) {
      e.preventDefault();
      onApply();
    }
  };

  return (
    <Wrapper className={className}>
      <Field data-error={error ? true : undefined} data-disabled={disabled || undefined}>
        <FieldStack>
          {value && placeholder && <InlineLabel>{placeholder}</InlineLabel>}
          <Input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            onKeyDown={handleKeyDown}
            placeholder={value ? undefined : placeholder}
            disabled={disabled}
            aria-label={placeholder}
            aria-invalid={Boolean(error)}
          />
        </FieldStack>
        <ApplyButton
          type="button"
          onClick={onApply}
          disabled={!canApply}
          data-active={canApply || undefined}
        >
          {applyLabel}
        </ApplyButton>
      </Field>

      {error && (
        <ErrorRow role="alert">
          <CloseIcon size={12} />
          <span>{error}</span>
        </ErrorRow>
      )}

      {successMessage && appliedList.length > 0 && (
        <SuccessRow>
          <SuccessCheck aria-hidden="true">
            <CheckIcon size={12} />
          </SuccessCheck>
          <span>{successMessage}</span>
        </SuccessRow>
      )}

      {appliedList.length > 0 && (
        <AppliedList>
          {appliedList.map(({ code, amount, description }) => {
            const showAmount = amount !== undefined;
            const showDescription = kind === 'voucher' && description !== undefined;
            return (
              <AppliedCard key={code}>
                <AppliedBody>
                  <AppliedCode>{code}</AppliedCode>
                  {showDescription && <AppliedDescription>{description}</AppliedDescription>}
                </AppliedBody>
                {showAmount && <AppliedAmount>{amount}</AppliedAmount>}
                {onRemove && (
                  <RemoveIconButton
                    type="button"
                    onClick={() => onRemove(code)}
                    aria-label={`${removeLabel} ${code}`}
                  >
                    <CloseIcon size={12} />
                  </RemoveIconButton>
                )}
              </AppliedCard>
            );
          })}
        </AppliedList>
      )}
    </Wrapper>
  );
}
